import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { Container } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { PageHeader } from "@/components/organisms";
import { Tab } from "@/components/organisms/Sidebar/types";
import { usePermissions } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { RequireAccess } from "@/routes/RequireAccess/RequireAccess";
import { Business } from "./Business/Business";
import { Documents } from "./Documents/Documents";
import { Marketing } from "./Marketing/Marketing";
import { Trip } from "./Trip/Trip";
import { UserAccounts } from "./UserAccounts/UserAccounts";

const pageTabs: Tab[] = [
  { name: "Business Settings", to: "business", icon: "" },
  {
    name: "Trip Settings",
    to: "trip",
    icon: "",
    permission: EPermission.ProviderConfigTripSettingsManage,
  },
  {
    name: "Marketing Tools",
    to: "marketing",
    icon: "",
    permission: EPermission.ProviderConfigMarketingToolManage,
  },
  { name: "Documents", to: "documents", icon: "" },
  {
    name: "User Accounts",
    to: "user-accounts",
    permission: [EPermission.ProviderAccountDirectoryManage, EPermission.ProviderConfigUserAccountsManage],
    icon: "",
  },
];

export const Config = () => {
  const navigate = useNavigate();
  const { hasPermission, hasAllPermissions } = usePermissions();
  const { pathname } = useLocation();
  const isParentPage = Boolean(
    matchPath({ path: "/config/:slug" }, pathname) || matchPath({ path: "operator/:operatorId/config/:slug" }, pathname)
  );
  const isTripCost = pathname.includes("/config/trip/trip-cost");
  const doubleBackRoutes = ["/config/trip/hourly-daily-hire", "/config/trip/dispatch-settings/edit/offloading"];
  const isDoubleBackRoute = doubleBackRoutes.some((route) => pathname.includes(route));

  const renderTabs = () => (
    <Tabs className="no-scrollbar">
      {pageTabs
        .filter((t) => {
          if (!t.permission) return true;
          return Array.isArray(t.permission) ? hasAllPermissions(t.permission) : hasPermission(t.permission);
        })
        .map((t, i) => (
          <Tabs.Tab key={i} to={t.to} name={t.name} />
        ))}
    </Tabs>
  );

  return (
    <>
      <ConfigHeader
        isTripCost={isTripCost}
        isParentPage={isParentPage}
        onBack={() =>
          isDoubleBackRoute ? navigate(pathname.split("/").slice(0, -2).join("/")) : navigate(pathname.split("/").slice(0, -1).join("/"))
        }
        renderTabs={renderTabs}
      />
      <ConfigContent isTripCost={isTripCost} />
    </>
  );
};

type HeaderProps = {
  isTripCost: boolean;
  isParentPage: boolean;
  onBack: () => void;
  renderTabs: () => React.ReactNode;
};

const ConfigHeader = ({ isTripCost, isParentPage, onBack, renderTabs }: HeaderProps) => {
  if (isTripCost) {
    return (
      <div className="-mx-3 border-b border-neutral-mid-gray lg:-mx-5">
        <Container className="w-full">
          <PageHeader.Actions onBack={onBack} className="px-3 pb-0 pt-3 lg:px-5" />
          <PageHeader className="!mx-0 px-3 lg:px-5" title="Trip Cost" skipCrumbs={3} />
          {isParentPage && renderTabs()}
        </Container>
      </div>
    );
  }

  return (
    <Container className="w-full">
      <PageHeader.Actions onBack={onBack} className="pb-0 pt-3" />
      <PageHeader title="Settings" skipCrumbs={1} />
      {isParentPage && <Tabs className="no-scrollbar mb-5 border-b !border-neutral-mid-gray">{renderTabs()}</Tabs>}
    </Container>
  );
};

const ConfigContent = ({ isTripCost }: { isTripCost: boolean }) => {
  if (isTripCost) {
    return (
      <div className="-mx-3 bg-neutral-surface-gray lg:-mx-5">
        <div className="relative px-3 lg:px-5">
          <Container className="w-full">
            <div className="flex flex-1 flex-col pb-12">
              <RequireAccess action="view" subject="providerConfig" />
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <Container className="w-full">
      <div className="flex flex-1 flex-col pb-12">
        <RequireAccess action="view" subject="providerConfig" />
      </div>
    </Container>
  );
};

Config.Trip = Trip;
Config.Business = Business;
Config.Marketing = Marketing;
Config.Documents = Documents;
Config.UserAccounts = UserAccounts;
